import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { formatINR } from "../lib/utils-format";
import {
    TrendingUp, ShieldCheck, Receipt, Wallet2,
    Users, GitMerge, ChevronRight, Package, Building2,
} from "lucide-react";

const TAB = { SALES: "sales", DEDUCTIONS: "deductions" };

/* Compute biz pair numbers from biz ledger meta (start_pair, biz_pairs, member_pairs) */
function getBizPairNumbers(meta) {
    if (!meta || meta.start_pair == null || meta.biz_pairs == null) return null;
    const total = (meta.biz_pairs ?? 0) + (meta.member_pairs ?? 0);
    const bizNums = [];
    for (let i = 0; i < total; i++) {
        const n = meta.start_pair + i;
        const isBiz = n >= 3 && n <= 25 && n % 2 === 1;
        if (isBiz) bizNums.push(n);
    }
    return bizNums;
}

const TYPE_LABEL = {
    admin_charge_deduction: { label: "Admin Charge",      cls: "bg-orange-100 text-orange-700" },
    tds_deduction:          { label: "TDS",               cls: "bg-red-100 text-red-600"       },
    pairing_bonus_credit:   { label: "Odd-Rule Pair Net", cls: "bg-blue-100 text-blue-700"     },
};

function SummaryCard({ icon: Icon, label, value, sub, accent, iconBg }) {
    return (
        <div className={`bg-white rounded-3xl p-5 shadow-sm border-l-4 ${accent} flex flex-col gap-3`}>
            <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest text-right leading-tight">{label}</span>
            </div>
            <div>
                <div className="text-2xl font-extrabold text-slate-900 tracking-tight font-mono">
                    {formatINR(value)}
                </div>
                {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
            </div>
        </div>
    );
}

function BreakdownBar({ label, admin, tds, total }) {
    if (!total) return null;
    const adminPct = Math.round((admin / total) * 100);
    const tdsPct   = Math.round((tds   / total) * 100);
    return (
        <div className="space-y-1.5">
            <div className="flex justify-between items-center text-xs">
                <span className="font-semibold text-slate-700">{label}</span>
                <span className="font-mono font-bold text-slate-600">{formatINR(admin + tds)}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                <div className="bg-orange-400 h-full rounded-l-full transition-all" style={{ width: `${adminPct}%` }} />
                <div className="bg-red-400 h-full rounded-r-full transition-all"   style={{ width: `${tdsPct}%`   }} />
            </div>
            <div className="flex gap-4 text-[10px] text-slate-400">
                <span><span className="text-orange-500 font-bold">Admin</span> {formatINR(admin)}</span>
                <span><span className="text-red-400 font-bold">TDS</span> {formatINR(tds)}</span>
            </div>
        </div>
    );
}

export default function AdminBusinessIncome() {
    const [data,    setData]    = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab,     setTab]     = useState(TAB.SALES);
    const [search,  setSearch]  = useState("");

    useEffect(() => {
        api.get("/admin/business-income")
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const s = data?.summary ?? {};
    const bd = data?.breakdown ?? {};

    const totalDeductions = s.total_deductions ?? 0;

    const filteredOrders = (data?.orders ?? []).filter(o =>
        !search.trim() ||
        o.member_name?.toLowerCase().includes(search.toLowerCase()) ||
        o.member_code?.toLowerCase().includes(search.toLowerCase()) ||
        o.product_name?.toLowerCase().includes(search.toLowerCase())
    );

    const filteredLedger = (data?.ledger ?? []).filter(l =>
        !search.trim() ||
        l.member_name?.toLowerCase().includes(search.toLowerCase()) ||
        l.member_code?.toLowerCase().includes(search.toLowerCase()) ||
        l.description?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <AppShell admin>
            <div className="space-y-6 pb-12">

                {/* ── Header ── */}
                <div className="flex flex-wrap justify-between items-start gap-4">
                    <div>
                        <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Business Finance</div>
                        <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5">
                            Business Income
                        </h1>
                        <p className="text-xs text-slate-400 mt-1">
                            Membership revenue minus all member payouts (net business income)
                        </p>
                    </div>
                    {!loading && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-[#1B2B6B] rounded-2xl text-white">
                            <TrendingUp className="w-4 h-4 text-emerald-400" />
                            <span className="text-sm font-bold">Net Business Income</span>
                            <span className="text-lg font-extrabold font-mono ml-1">{formatINR(s.grand_total ?? 0)}</span>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-48 text-slate-400 text-sm">Loading…</div>
                ) : (
                <>
                {/* ── Summary cards ── */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <SummaryCard
                        icon={Package}
                        label="Membership Revenue"
                        value={s.membership_revenue ?? 0}
                        sub={`${(data?.orders ?? []).length} paid memberships`}
                        accent="border-emerald-400"
                        iconBg="bg-emerald-100 text-emerald-600"
                    />
                    <SummaryCard
                        icon={ShieldCheck}
                        label="Admin Charges (15%)"
                        value={s.admin_charges ?? 0}
                        sub="Collected from all bonuses"
                        accent="border-orange-400"
                        iconBg="bg-orange-100 text-orange-600"
                    />
                    <SummaryCard
                        icon={Receipt}
                        label="TDS Collected (5%)"
                        value={s.tds_collected ?? 0}
                        sub="Tax deducted at source"
                        accent="border-red-400"
                        iconBg="bg-red-100 text-red-500"
                    />
                    <SummaryCard
                        icon={TrendingUp}
                        label="Net Business Income"
                        value={s.grand_total ?? 0}
                        sub="Membership revenue − member payouts"
                        accent="border-[#2172E5]"
                        iconBg="bg-blue-100 text-[#2172E5]"
                    />
                </div>

                {/* ── Breakdown + source split ── */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

                    {/* Deduction breakdown by source */}
                    <div className="lg:col-span-4 bg-white rounded-3xl p-6 shadow-sm space-y-5">
                        <div>
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Deduction Source</span>
                            <h3 className="text-base font-extrabold text-slate-900 mt-0.5">Bonus Deduction Split</h3>
                        </div>
                        <div className="flex gap-4 text-[10px] text-slate-400 mb-1">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />Admin 15%</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" />TDS 5%</span>
                        </div>
                        <div className="space-y-5">
                            <BreakdownBar
                                label="From Direct Referral Bonuses"
                                admin={bd.from_direct_bonuses?.admin  ?? 0}
                                tds={bd.from_direct_bonuses?.tds    ?? 0}
                                total={totalDeductions}
                            />
                            <BreakdownBar
                                label="From Pairing Bonuses"
                                admin={bd.from_pairing_bonuses?.admin ?? 0}
                                tds={bd.from_pairing_bonuses?.tds   ?? 0}
                                total={totalDeductions}
                            />
                        </div>
                        <div className="pt-4 border-t border-slate-100 space-y-1.5 text-xs">
                            <div className="flex justify-between">
                                <span className="text-slate-500">Total Admin Collected</span>
                                <span className="font-bold text-orange-600 font-mono">{formatINR(s.admin_charges)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-500">Total TDS Collected</span>
                                <span className="font-bold text-red-500 font-mono">{formatINR(s.tds_collected)}</span>
                            </div>
                            <div className="flex justify-between border-t border-slate-100 pt-1.5">
                                <span className="font-bold text-slate-700">Combined</span>
                                <span className="font-extrabold text-[#2172E5] font-mono">{formatINR(s.total_deductions)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Revenue composition */}
                    <div className="lg:col-span-8 bg-white rounded-3xl p-6 shadow-sm">
                        <div className="mb-5">
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Revenue Composition</span>
                            <h3 className="text-base font-extrabold text-slate-900 mt-0.5">Total Business Income Breakdown</h3>
                        </div>
                        {(() => {
                            const grandTotal = s.grand_total || 1;
                            const memberPct  = Math.round(((s.membership_net_after_bonuses ?? 0) / grandTotal) * 100);
                            const adminPct   = Math.round(((s.admin_charges                ?? 0) / grandTotal) * 100);
                            const tdsPct     = Math.round(((s.tds_collected                ?? 0) / grandTotal) * 100);
                            const bizPct     = Math.round(((s.odd_rule_pairing_net         ?? 0) / grandTotal) * 100);
                            return (
                                <div className="space-y-4">
                                    {/* Stacked bar */}
                                    <div className="h-7 rounded-2xl overflow-hidden flex gap-0.5">
                                        <div className="bg-emerald-400 h-full transition-all flex items-center justify-center" style={{ width: `${memberPct}%` }}>
                                            {memberPct > 10 && <span className="text-[9px] text-white font-bold">{memberPct}%</span>}
                                        </div>
                                        <div className="bg-orange-400 h-full transition-all flex items-center justify-center" style={{ width: `${adminPct}%` }}>
                                            {adminPct > 5 && <span className="text-[9px] text-white font-bold">{adminPct}%</span>}
                                        </div>
                                        <div className="bg-red-400 h-full transition-all flex items-center justify-center" style={{ width: `${tdsPct}%` }}>
                                            {tdsPct > 3 && <span className="text-[9px] text-white font-bold">{tdsPct}%</span>}
                                        </div>
                                        <div className="bg-blue-400 h-full transition-all flex items-center justify-center" style={{ width: `${bizPct}%` }}>
                                            {bizPct > 3 && <span className="text-[9px] text-white font-bold">{bizPct}%</span>}
                                        </div>
                                    </div>
                                    {/* Legend rows */}
                                    <div className="space-y-3">
                                        {[
                                            { color: "bg-emerald-400", label: "Membership Net (after member payouts)",  amount: s.membership_net_after_bonuses ?? 0, pct: memberPct, icon: Users },
                                            { color: "bg-orange-400",  label: "Admin Charges (15% of bonuses)",         amount: s.admin_charges                ?? 0, pct: adminPct,  icon: ShieldCheck },
                                            { color: "bg-red-400",     label: "TDS Deducted (5% of bonuses)",           amount: s.tds_collected                ?? 0, pct: tdsPct,    icon: Receipt },
                                            { color: "bg-blue-400",    label: "Odd-Rule Pairing Net (pairs #3,5…#25)",  amount: s.odd_rule_pairing_net         ?? 0, pct: bizPct,    icon: GitMerge },
                                        ].map(({ color, label, amount, pct, icon: Ic }) => (
                                            <div key={label} className="flex items-center gap-3">
                                                <span className={`w-3 h-3 rounded-full flex-shrink-0 ${color}`} />
                                                <Ic className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                                <span className="flex-1 text-xs text-slate-600">{label}</span>
                                                <span className="text-[10px] text-slate-400 w-8 text-right">{pct}%</span>
                                                <span className="text-xs font-mono font-bold text-slate-800 w-24 text-right">{formatINR(amount)}</span>
                                            </div>
                                        ))}
                                        <div className="flex items-center gap-3 border-t border-slate-100 pt-3">
                                            <span className="w-3 h-3 flex-shrink-0" />
                                            <TrendingUp className="w-3.5 h-3.5 text-[#2172E5] flex-shrink-0" />
                                            <span className="flex-1 text-xs font-bold text-slate-800">Net Business Income</span>
                                            <span className="text-[10px] text-slate-400 w-8 text-right">100%</span>
                                            <span className="text-sm font-mono font-extrabold text-[#2172E5] w-24 text-right">{formatINR(s.grand_total ?? 0)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* ── Tabs ── */}
                <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
                    {/* Tab header */}
                    <div className="flex items-center justify-between px-6 pt-5 pb-0 border-b border-slate-100">
                        <div className="flex gap-1">
                            {[
                                { id: TAB.SALES,       icon: Package,   label: "Membership Sales",     count: (data?.orders ?? []).length },
                                { id: TAB.DEDUCTIONS,  icon: GitMerge,  label: "Bonus Deductions",     count: (data?.ledger ?? []).length },
                            ].map(({ id, icon: Ic, label, count }) => (
                                <button
                                    key={id}
                                    onClick={() => { setTab(id); setSearch(""); }}
                                    className={`flex items-center gap-2 px-4 py-3 text-xs font-bold border-b-2 transition-all -mb-px ${
                                        tab === id
                                            ? "border-[#2172E5] text-[#2172E5]"
                                            : "border-transparent text-slate-400 hover:text-slate-600"
                                    }`}
                                >
                                    <Ic className="w-3.5 h-3.5" />
                                    {label}
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-extrabold ${
                                        tab === id ? "bg-[#2172E5]/10 text-[#2172E5]" : "bg-slate-100 text-slate-400"
                                    }`}>{count}</span>
                                </button>
                            ))}
                        </div>
                        <input
                            type="text"
                            placeholder="Search…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="mb-3 px-3 py-1.5 bg-slate-50 rounded-xl text-xs border border-slate-100 outline-none focus:ring-2 focus:ring-[#2172E5] w-44 transition-all"
                        />
                    </div>

                    {/* ── Membership Sales table ── */}
                    {tab === TAB.SALES && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        {["Member", "Code", "Product", "Amount", "Date"].map(h => (
                                            <th key={h} className="px-6 py-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filteredOrders.length === 0 && (
                                        <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No records found.</td></tr>
                                    )}
                                    {filteredOrders.map((o, i) => (
                                        <tr key={o.id} className={`hover:bg-blue-50/20 transition-colors ${i % 2 === 1 ? "bg-slate-50/40" : ""}`}>
                                            <td className="px-6 py-3.5 font-semibold text-slate-800">{o.member_name}</td>
                                            <td className="px-6 py-3.5 font-mono text-[10px] text-slate-400 tracking-widest">{o.member_code}</td>
                                            <td className="px-6 py-3.5 text-slate-600">{o.product_name}</td>
                                            <td className="px-6 py-3.5 font-mono font-bold text-emerald-600 tabular-nums">{formatINR(o.amount)}</td>
                                            <td className="px-6 py-3.5 text-slate-400 whitespace-nowrap">
                                                {o.paid_at ? new Date(o.paid_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                {filteredOrders.length > 0 && (
                                    <tfoot>
                                        <tr className="border-t-2 border-slate-100 bg-emerald-50/60">
                                            <td colSpan={3} className="px-6 py-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                                                Total ({filteredOrders.length} memberships)
                                            </td>
                                            <td className="px-6 py-3 font-mono font-extrabold text-emerald-600 tabular-nums">
                                                {formatINR(filteredOrders.reduce((s, o) => s + o.amount, 0))}
                                            </td>
                                            <td />
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}

                    {/* ── Bonus Deductions table ── */}
                    {tab === TAB.DEDUCTIONS && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        {["Type", "Source", "Member / Reference", "Amount", "Date"].map(h => (
                                            <th key={h} className="px-6 py-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filteredLedger.length === 0 && (
                                        <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No records found.</td></tr>
                                    )}
                                    {filteredLedger.map((l, i) => {
                                        const badge = TYPE_LABEL[l.type] ?? { label: l.type, cls: "bg-slate-100 text-slate-600" };
                                        return (
                                            <tr key={l.id} className={`hover:bg-blue-50/20 transition-colors ${i % 2 === 1 ? "bg-slate-50/40" : ""}`}>
                                                <td className="px-6 py-3.5">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide ${badge.cls}`}>
                                                        {badge.label}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3.5">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase ${
                                                        l.source === "odd_rule_pairing"
                                                            ? "bg-blue-100 text-blue-700"
                                                            : l.source === "pairing"
                                                            ? "bg-[#7059FD]/10 text-[#7059FD]"
                                                            : "bg-[#10B1B6]/10 text-[#10B1B6]"
                                                    }`}>
                                                        {l.source === "odd_rule_pairing" || l.source === "pairing"
                                                            ? <GitMerge className="w-2.5 h-2.5" />
                                                            : <Users className="w-2.5 h-2.5" />}
                                                        {l.source === "odd_rule_pairing"
                                                            ? `Odd-Rule ${l.source_date ?? ""}`
                                                            : l.source === "pairing"
                                                            ? `Pairing ${l.source_date ?? ""}`
                                                            : "Direct"}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-3.5 text-slate-700">
                                                    {l.member_name
                                                        ? <span>{l.member_name} <span className="text-slate-400 font-mono text-[10px]">{l.member_code}</span></span>
                                                        : <span className="text-slate-400 font-mono text-[10px]">{l.reference_id}</span>
                                                    }
                                                    {l.source === "odd_rule_pairing" && (() => {
                                                        const bizNums = getBizPairNumbers(l.meta);
                                                        if (!bizNums?.length) return null;
                                                        return (
                                                            <div className="mt-1 flex flex-wrap items-center gap-1">
                                                                <span className="text-[9px] font-bold text-amber-600 uppercase tracking-wider">Biz pairs:</span>
                                                                {bizNums.map(n => (
                                                                    <span key={n} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                                                        <Building2 className="w-2 h-2" />#{n}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td className={`px-6 py-3.5 font-mono font-bold tabular-nums ${
                                                    l.type === "pairing_bonus_credit" ? "text-blue-600"
                                                    : l.type === "admin_charge_deduction" ? "text-orange-600"
                                                    : "text-red-500"
                                                }`}>
                                                    {formatINR(l.amount)}
                                                </td>
                                                <td className="px-6 py-3.5 text-slate-400 whitespace-nowrap">
                                                    {new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                {filteredLedger.length > 0 && (() => {
                                    const adminTotal  = filteredLedger.filter(l => l.type === "admin_charge_deduction").reduce((s, l) => s + l.amount, 0);
                                    const tdsTotal    = filteredLedger.filter(l => l.type === "tds_deduction").reduce((s, l) => s + l.amount, 0);
                                    const bizPairTotal = filteredLedger.filter(l => l.type === "pairing_bonus_credit").reduce((s, l) => s + l.amount, 0);
                                    return (
                                        <tfoot>
                                            <tr className="border-t-2 border-slate-100 bg-slate-50/80">
                                                <td colSpan={3} className="px-6 py-3 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                                                    Totals ({filteredLedger.length} entries)
                                                    <span className="ml-3 text-orange-500">Admin {formatINR(adminTotal)}</span>
                                                    <span className="ml-2 text-red-400">TDS {formatINR(tdsTotal)}</span>
                                                    {bizPairTotal > 0 && <span className="ml-2 text-blue-500">Odd-Rule Pairs {formatINR(bizPairTotal)}</span>}
                                                </td>
                                                <td className="px-6 py-3 font-mono font-extrabold text-slate-700 tabular-nums">
                                                    {formatINR(adminTotal + tdsTotal + bizPairTotal)}
                                                </td>
                                                <td />
                                            </tr>
                                        </tfoot>
                                    );
                                })()}
                            </table>
                        </div>
                    )}
                </div>
                </>
                )}
            </div>
        </AppShell>
    );
}
